import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "./api";

// ═══════════════════════════════════════════════════════════════════════════
//  DATA FETCHING
// ═══════════════════════════════════════════════════════════════════════════
// Small on purpose — every page here is "GET one endpoint, show it, refetch
// when the range changes". A query library would be more machinery than the
// whole console needs.
//
// The one behaviour worth having is the one below: on a refetch the PREVIOUS
// data is kept and marked stale rather than thrown away for a skeleton. Every
// panel changes size with its content, so a skeleton flash means the page
// jumps, the scroll position moves, and whatever was being read is gone.

export interface Query<T> {
  data: T | null;
  error: string | null;
  /** True only for the very first load — there is nothing to show yet. */
  loading: boolean;
  /** True while refetching over data that is already on screen. */
  stale: boolean;
  reload: () => void;
}

export function useApi<T>(
  path: string | null,
  query?: Record<string, string | number | boolean | undefined>,
): Query<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Serialised so the effect compares by value: a fresh object literal on
  // every render would otherwise refetch forever.
  const queryKey = JSON.stringify(query ?? {});

  // Held in a ref so `pending` can flip without re-running the effect.
  const hasData = useRef(false);

  // ── Keeping data across a refetch, but not across a subject change ──────
  // A new `query` on the same endpoint is the same view over a different
  // slice, so the old render is worth holding at reduced opacity. A new PATH
  // is a different thing entirely — navigating from customer A to customer B
  // would otherwise show A's name, phone and renders under B's heading until
  // the request landed, which is not a loading state, it is wrong data.
  const lastPath = useRef(path);
  if (lastPath.current !== path) {
    lastPath.current = path;
    hasData.current = false;
  }

  useEffect(() => {
    // A null path means "nothing to fetch yet" — a caller gating on a role or
    // a route param. It must settle, or the panel spins forever.
    if (!path) {
      setPending(false);
      return;
    }

    const controller = new AbortController();
    setPending(true);

    // Clear the previous subject's data on the way out, so a failed load for
    // customer B cannot leave customer A's row on screen.
    if (!hasData.current) setData(null);

    api<T>(path, { query: JSON.parse(queryKey), signal: controller.signal })
      .then((result) => {
        setData(result);
        hasData.current = true;
        setError(null);
      })
      .catch((err) => {
        // An aborted request is this effect cleaning up after itself, not a
        // failure — surfacing it would flash an error on every range change.
        if (err instanceof DOMException && err.name === "AbortError") return;
        // A 401 is already handled globally (the shell bounces to login);
        // showing "session ended" in a panel as well is just noise.
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof Error ? err.message : "Something went wrong");
      })
      .finally(() => {
        if (!controller.signal.aborted) setPending(false);
      });

    return () => controller.abort();
  }, [path, queryKey, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    data,
    error,
    loading: pending && !hasData.current,
    stale: pending && hasData.current,
    reload,
  };
}
